import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ProtectedRoute — the auth / role / permission / module gate on every page.
 *
 * Module licensing (requiredModule / requiredAnyModule) does not hide a page:
 * a tenant that has not bought the module can still open it, and sees
 * <ModuleLocked> — inside the app chrome, so the sidebar and header stay —
 * INSTEAD of the page. The page component is never mounted, so it never
 * fetches and none of its data reaches the DOM; the services' own 403
 * module_not_licensed is the backstop. The overlay itself is presentation.
 *
 * Licensing rules (aliases, core modules, super admins, an empty module list,
 * the ui_module_lock off-switch) live in licensing/moduleLicense.js.
 */
import { Navigate, useLocation } from 'react-router-dom';

import { authService } from '../../services/auth/authService';
import { isFeatureEnabled } from '../../utils/featureFlags';
import { hasAnyPermission, hasPermission, hasRequiredRole } from '../../utils/permissions';
import useModuleLicense from '../../licensing/useModuleLicense';
import AppLayout from '../layout/AppLayout';
import ModuleLocked from '../licensing/ModuleLocked';
import { InsideAppLayoutContext } from '../licensing/ModuleLockBoundary';
import PageLoadingFallback from './PageLoadingFallback';

const AccessDeniedPopup = () => {
  const { t } = useTranslation('commonUi');

  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        role="alertdialog"
        aria-live="assertive"
        aria-label={t('commonUi:commonProtectedroute.permissionDenied')}
        style={{
          width: '100%',
          maxWidth: '540px',
          background: '#121a2f',
          color: '#ffffff',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '12px',
          padding: '1.25rem 1.5rem',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.35)',
        }}
      >
        <h5 style={{ margin: 0, marginBottom: '0.65rem' }}>{t('commonUi:commonProtectedroute.permissionDenied')}</h5>
        <p style={{ margin: 0, opacity: 0.9 }}> {t('commonUi:commonProtectedroute.permissionIsNotGivenByThe')} </p>
      </div>
    </div>
  );
};

const ProtectedRoute = ({
  children,
  requiredRole,
  requiredPermission,
  requiredAnyPermission,
  requiredPermissionModule,
  requiredModule,
  requiredAnyModule,
  requiredFeature,
  requiredOrgIds,        // optional: array of org_ids permitted to view; pre-GA gate
}) => {
  const location = useLocation();
  const license = useModuleLicense();
  const insideLayout = useContext(InsideAppLayoutContext);
  const token = localStorage.getItem('jwt_token');

  if (!token) {
    // Remember where the visitor was going, so signing in brings them back
    // here rather than to the home page. Router state covers a password
    // sign-in; sessionStorage is what survives an SSO sign-in, which leaves the
    // app for the identity provider. The CLI's approval pages (/device,
    // /oauth/authorize) depend on this: without it the person signs in, lands
    // elsewhere, and the terminal waits until its code expires.
    authService.rememberReturnAfterLogin(`${location.pathname}${location.search}${location.hash}`);
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  let user = {};
  try {
    user = JSON.parse(localStorage.getItem('user') || '{}');
  } catch {}

  const normalizedUser =
    user && typeof user === 'object' && user.user && typeof user.user === 'object'
      ? { ...user.user, ...user }
      : user;

  const permissionModule = requiredPermissionModule || null;

  // The role rule lives in utils/permissions (hasRequiredRole) so the links to a
  // role-gated page (e.g. Billing in the account menu) decide the same way.
  if (requiredRole && !hasRequiredRole(requiredRole, normalizedUser)) {
    return <AccessDeniedPopup />;
  }

  if (requiredPermission) {
    if (!hasPermission(requiredPermission, { module: permissionModule, user: normalizedUser })) {
      return <AccessDeniedPopup />;
    }
  }

  if (Array.isArray(requiredAnyPermission) && requiredAnyPermission.length > 0) {
    if (!hasAnyPermission(requiredAnyPermission, { module: permissionModule, user: normalizedUser })) {
      return <AccessDeniedPopup />;
    }
  }

  // Module licence gate (see the header comment). Evaluated after the role and
  // permission gates, which still deny outright: a user who may not use a page
  // learns nothing more from its lock screen.
  const requiredModules = [
    ...(requiredModule ? [requiredModule] : []),
    ...(Array.isArray(requiredAnyModule) ? requiredAnyModule : []),
  ];
  // requiredModule must be held; requiredAnyModule needs any one of its list.
  const lockedModule = (requiredModule && !license.isLicensed(requiredModule))
    ? license.canonical(requiredModule) || String(requiredModule)
    : (Array.isArray(requiredAnyModule) && requiredAnyModule.length > 0
      ? license.firstLocked(requiredAnyModule)
      : null);

  if (requiredModules.length > 0 && lockedModule) {
    // Until the catalog answers, the auth service's off-switch may be set:
    // hold a page we would lock rather than flash a lock screen. Pages the org
    // holds never wait.
    const screen = license.ready
      ? <ModuleLocked module={lockedModule} />
      : <PageLoadingFallback />;
    return insideLayout ? screen : <AppLayout>{screen}</AppLayout>;
  }

  // Feature-flag gate (org-scoped). Redirect home if the flag is off for this user.
  if (requiredFeature && !isFeatureEnabled(requiredFeature, normalizedUser)) {
    return <AccessDeniedPopup />;
  }

  // Pre-GA / private-preview gate: only specific tenant org_ids may view.
  if (Array.isArray(requiredOrgIds) && requiredOrgIds.length > 0) {
    const orgId = Number(
      normalizedUser.organization_id ?? normalizedUser.org_id ?? normalizedUser.organizationId,
    );
    if (!Number.isFinite(orgId) || !requiredOrgIds.includes(orgId)) {
      return <AccessDeniedPopup />;
    }
  }

  return children;
};

export default ProtectedRoute;
